// src/shared/components/DevMobileEmulator/DeviceModeContext.tsx
//
// React context that signals "force mobile layout" to the entire tree.
// When active, Layout hides the sidebar and BubbleNav renders unconditionally
// — regardless of the real browser viewport width.
//
// Only the DevMobileEmulator provider sets this to true.
// In production or normal dev, it defaults to false (no overhead).

import { createContext, useContext, type ReactNode } from 'react';

interface DeviceModeValue {
  /** True when running inside the phone emulator frame */
  readonly forceMobile: boolean;
}

const DeviceModeCtx = createContext<DeviceModeValue>({ forceMobile: false });

export function DeviceModeProvider({
  forceMobile,
  children,
}: {
  forceMobile: boolean;
  children: ReactNode;
}) {
  return (
    <DeviceModeCtx.Provider value={{ forceMobile }}>
      {children}
    </DeviceModeCtx.Provider>
  );
}

/**
 * Returns true when the app must behave as mobile.
 * True if: (a) DeviceModeProvider says forceMobile, or
 *          (b) real viewport is below `md` breakpoint (< 768px landscape).
 * Components should use this INSTEAD of raw Tailwind `md:` for layout-level
 * decisions (sidebar vs BubbleNav, padding, etc.).
 */
export function useIsMobile(): boolean {
  const { forceMobile } = useContext(DeviceModeCtx);
  return forceMobile;
  // Note: CSS media queries (Tailwind `md:`) still use real viewport.
  // This hook covers JS-side decisions; CSS overrides are handled
  // by a class on the emulator container.
}

export function useDeviceMode(): DeviceModeValue {
  return useContext(DeviceModeCtx);
}
